import React, { useMemo, useState } from 'react';
import { Plus, Search, X, Edit2, Save, Eye } from 'lucide-react';
import { validateSupplier } from '../lib/validators';

// Helper functions for managing order charges
const buildChargeId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

const normalizeOrderCharge = (charge = {}, { autoLabel = true } = {}) => {
  const kind = charge.kind || 'external';
  const defaultLabel = 'Cargo de pedido';
  const label = autoLabel
    ? (charge.label && charge.label !== '' ? charge.label : defaultLabel)
    : (charge.label || defaultLabel);
  return {
    id: charge.id || buildChargeId(),
    type: charge.type || 'flat',
    value: charge.value ?? '',
    mode: charge.mode || 'on_top',
    kind,
    label,
  };
};

const serializeOrderCharges = (charges = []) => {
  return (Array.isArray(charges) ? charges : [])
    .map((charge) => {
      const valueNum = Number(charge.value || 0) || 0;
      if (!valueNum) return null;
      const label = charge.label && charge.label !== '' ? charge.label : 'Cargo de pedido';
      return {
        id: charge.id || buildChargeId(),
        type: charge.type || 'flat',
        value: valueNum,
        mode: charge.mode || 'on_top',
        kind: charge.kind || 'external',
        label,
      };
    })
    .filter(Boolean);
};

const describeCharge = (charge) => {
  const label = charge.label || 'Cargo de pedido';
  const isPercent = (charge.type || 'flat') === 'percent';
  const valueNum = Number(charge.value || 0) || 0;
  return `${label} · ${isPercent ? `${valueNum}%` : `$${valueNum.toLocaleString('en-US')}`}`;
};

const Suppliers = ({ suppliers, transactions = [], onUpdateSupplier, onAddSupplier, onDeleteSupplier }) => {
  const [showModal, setShowModal] = useState(false);
  const [editModal, setEditModal] = useState({ show: false, supplier: null });
  const [newSupplier, setNewSupplier] = useState({
    name: '',
    contact: '',
    phone: '',
    accountName: '',
    purchasePrice: '',
    defaultOrderCharges: [],
  });
  const [searchTerm, setSearchTerm] = useState('');
  const newErrors = useMemo(() => validateSupplier(newSupplier), [newSupplier]);
  const editErrors = useMemo(() => validateSupplier(editModal?.supplier || {}), [editModal?.supplier]);

  const filteredSuppliers = suppliers.filter(supplier =>
    supplier.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    supplier.contact.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (supplier.accountName && supplier.accountName.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (editModal.supplier) {
      setEditModal(prev => ({
        ...prev,
        supplier: { ...prev.supplier, [name]: value }
      }));
    } else {
      setNewSupplier(prev => ({ ...prev, [name]: value }));
    }
  };

  // Charge management for new supplier
  const handleAddNewCharge = () => {
    setNewSupplier(prev => ({
      ...prev,
      defaultOrderCharges: [
        ...(Array.isArray(prev.defaultOrderCharges) ? prev.defaultOrderCharges : []),
        normalizeOrderCharge({ type: 'flat', value: '', kind: 'external', label: 'Cargo de pedido' }, { autoLabel: false }),
      ],
    }));
  };

  const updateNewSupplierCharge = (index, patch) => {
    setNewSupplier(prev => {
      const current = Array.isArray(prev.defaultOrderCharges) ? prev.defaultOrderCharges : [];
      const next = current.map((charge, idx) =>
        idx === index ? normalizeOrderCharge({ ...charge, ...patch }, { autoLabel: false }) : charge
      );
      return { ...prev, defaultOrderCharges: next };
    });
  };

  const handleRemoveNewCharge = (index) => {
    setNewSupplier(prev => ({
      ...prev,
      defaultOrderCharges: (Array.isArray(prev.defaultOrderCharges) ? prev.defaultOrderCharges : []).filter((_, idx) => idx !== index),
    }));
  };

  // Charge management for edit supplier
  const handleAddEditCharge = () => {
    setEditModal(prev => ({
      ...prev,
      supplier: {
        ...prev.supplier,
        defaultOrderCharges: [
          ...(Array.isArray(prev.supplier.defaultOrderCharges) ? prev.supplier.defaultOrderCharges : []),
          normalizeOrderCharge({ type: 'flat', value: '', kind: 'external', label: 'Cargo de pedido' }, { autoLabel: false }),
        ],
      },
    }));
  };

  const updateEditSupplierCharge = (index, patch) => {
    setEditModal(prev => {
      const current = Array.isArray(prev.supplier.defaultOrderCharges) ? prev.supplier.defaultOrderCharges : [];
      const next = current.map((charge, idx) =>
        idx === index ? normalizeOrderCharge({ ...charge, ...patch }, { autoLabel: false }) : charge
      );
      return { ...prev, supplier: { ...prev.supplier, defaultOrderCharges: next } };
    });
  };

  const handleRemoveEditCharge = (index) => {
    setEditModal(prev => ({
      ...prev,
      supplier: {
        ...prev.supplier,
        defaultOrderCharges: (Array.isArray(prev.supplier.defaultOrderCharges) ? prev.supplier.defaultOrderCharges : []).filter((_, idx) => idx !== index),
      },
    }));
  };

  const handleSubmitNew = (e) => {
    e.preventDefault();
    if (Array.isArray(newErrors) && newErrors.length > 0) return;
    // Lógica para añadir nuevo proveedor: llamar al handler del padre si existe
    if (onAddSupplier) {
      const parsed = {
        ...newSupplier,
        purchasePrice: newSupplier.purchasePrice !== '' ? Number(newSupplier.purchasePrice) : undefined,
        defaultOrderCharges: serializeOrderCharges(newSupplier.defaultOrderCharges),
      };
      onAddSupplier(parsed);
    } else {
      console.log("Nuevo Proveedor:", newSupplier);
    }
    setShowModal(false);
    setNewSupplier({
      name: '',
      contact: '',
      phone: '',
      accountName: '',
      purchasePrice: '',
      defaultOrderCharges: [],
    });
  };

  const handleEditSupplier = (supplierId) => {
    const supplier = suppliers.find(s => s.id === supplierId);
    if (supplier) {
      setEditModal({ show: true, supplier: { ...supplier } });
    }
  };

  const handleUpdate = (e) => {
    e.preventDefault();
    if (onUpdateSupplier) {
      const parsed = {
        ...editModal.supplier,
        defaultOrderCharges: serializeOrderCharges(editModal.supplier.defaultOrderCharges),
      };
      onUpdateSupplier(parsed);
    } else {
      console.log("Proveedor actualizado:", editModal.supplier);
    }
    setEditModal({ show: false, supplier: null });
  };

  const handleViewDetails = (supplier) => {
    setEditModal({ show: true, supplier: { ...supplier, readOnly: true } });
  };

  return (
  <div className="p-6 bg-gray-50 dark:bg-gray-900 min-h-screen">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800 dark:text-white">Proveedores</h1>
        <button 
          onClick={() => setShowModal(true)}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center hover:bg-blue-700 transition-colors"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nuevo Proveedor
        </button>
      </div>

  <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700 mb-6">
        <div className="relative max-w-md">
          <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-400" />
          <input 
            type="text" 
            placeholder="Buscar proveedores o contactos..." 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 pr-4 py-2 w-full border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          />
        </div>
      </div>

  <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-700/50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Proveedor</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Contacto</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Teléfono</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Cuenta de Depósito</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Cargos por defecto</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-800">
            {filteredSuppliers.map((supplier) => (
              <tr key={supplier.id} className="hover:bg-gray-50 hover:dark:bg-gray-700/60">
                <td className="px-6 py-4">
                  <div className="text-sm font-medium text-gray-900 dark:text-white">{supplier.name}</div>
                </td>
                <td className="px-6 py-4 text-sm text-gray-900 dark:text-white">{supplier.contact}</td>
                <td className="px-6 py-4 text-sm text-gray-900 dark:text-white">{supplier.phone}</td>
                <td className="px-6 py-4">
                  <span className="text-sm font-medium text-blue-600 dark:text-blue-300 bg-blue-50 dark:bg-blue-900/30 px-2 py-1 rounded">
                    {supplier.accountName || 'No asignada'}
                  </span>
                </td>
                <td className="px-6 py-4">
                  <div className="flex flex-wrap gap-1">
                    {(Array.isArray(supplier.defaultOrderCharges) && supplier.defaultOrderCharges.length > 0) ? (
                      supplier.defaultOrderCharges.map((charge, idx) => (
                        <span key={charge.id || idx} className="text-xs px-2 py-1 rounded bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300">
                          {describeCharge(charge)}
                        </span>
                      ))
                    ) : (
                      <span className="text-xs text-gray-500 dark:text-gray-400">Sin cargos</span>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <button 
                    onClick={() => handleViewDetails(supplier)}
                    className="text-blue-600 dark:text-blue-400 hover:text-blue-900 hover:dark:text-blue-300 mr-3 flex items-center"
                    title="Ver detalles"
                  >
                    <Eye className="w-4 h-4 mr-1" />
                    Ver
                  </button>
                  <button 
                    onClick={() => handleEditSupplier(supplier.id)}
                    className="text-green-600 dark:text-green-400 hover:text-green-900 hover:dark:text-green-300 mr-3 flex items-center"
                    title="Editar proveedor"
                  >
                    <Edit2 className="w-4 h-4 mr-1" />
                    Editar
                  </button>
                  <button
                    onClick={() => {
                      if (window.confirm(`¿Eliminar proveedor ${supplier.name}? Esta acción no se puede deshacer.`)) {
                        if (typeof onDeleteSupplier === 'function') onDeleteSupplier(supplier.id);
                      }
                    }}
                    className="text-red-600 dark:text-red-400 hover:text-red-800 hover:dark:text-red-300 flex items-center"
                    title="Eliminar proveedor"
                  >
                    Eliminar
                  </button>
                </td>
              </tr>
            ))}
            {filteredSuppliers.length === 0 && (
              <tr>
                <td colSpan="6" className="px-6 py-8 text-center text-gray-500 dark:text-gray-400">
                  No hay proveedores para mostrar. ¡Agrega uno nuevo!
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Modal para nuevo proveedor */}
      {showModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg w-full max-w-md relative">
            <button 
              onClick={() => setShowModal(false)}
              className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 hover:dark:text-gray-300"
            >
              <X className="w-6 h-6" />
            </button>
            <h2 className="text-xl font-bold text-gray-800 dark:text-white mb-4">Nuevo Proveedor</h2>
            <form onSubmit={handleSubmitNew} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Nombre del Proveedor</label>
                <input
                  type="text"
                  name="name"
                  value={newSupplier.name}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="Ej: Proveedor Confiable S.A."
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Contacto Principal</label>
                <input
                  type="text"
                  name="contact"
                  value={newSupplier.contact}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="Ej: Roberto Sánchez"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Teléfono</label>
                <input
                  type="text"
                  name="phone"
                  value={newSupplier.phone}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="Ej: 555-0789"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Cuenta de Depósito</label>
                <input
                  type="text"
                  name="accountName"
                  value={newSupplier.accountName}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="Ej: Firsbel"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Precio que nos cobra (compra)</label>
                <input
                  type="number"
                  name="purchasePrice"
                  value={newSupplier.purchasePrice}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="Ej: 500000"
                  step="0.01"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">Cargos sugeridos para pedidos</label>
                <div className="space-y-2">
                  {(Array.isArray(newSupplier.defaultOrderCharges) ? newSupplier.defaultOrderCharges : []).map((charge, idx) => (
                    <div key={charge.id || idx} className="grid grid-cols-12 gap-2 p-2 border border-gray-200 dark:border-gray-600 rounded">
                      <select
                        value={charge.type || 'flat'}
                        onChange={(e) => updateNewSupplierCharge(idx, { type: e.target.value })}
                        className="col-span-3 text-xs px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      >
                        <option value="percent">Porcentaje</option>
                        <option value="flat">Monto fijo</option>
                      </select>
                      <input
                        type="number"
                        value={charge.value || ''}
                        onChange={(e) => updateNewSupplierCharge(idx, { value: e.target.value })}
                        placeholder={charge.type === 'percent' ? '%' : '$'}
                        className="col-span-3 text-xs px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        step="0.01"
                      />
                      <input
                        type="text"
                        value={charge.label || ''}
                        onChange={(e) => updateNewSupplierCharge(idx, { label: e.target.value })}
                        placeholder="Etiqueta"
                        className="col-span-5 text-xs px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      />
                      <button
                        type="button"
                        onClick={() => handleRemoveNewCharge(idx)}
                        className="col-span-1 text-red-600 dark:text-red-400 hover:text-red-800 hover:dark:text-red-300 flex items-center justify-center"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={handleAddNewCharge}
                    className="w-full text-sm py-2 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded text-gray-600 dark:text-gray-300 hover:border-blue-400 hover:text-blue-600 hover:dark:border-blue-500 hover:dark:text-blue-400 transition-colors"
                  >
                    + Agregar cargo
                  </button>
                </div>
              </div>
              <button
                type="submit"
                disabled={Array.isArray(newErrors) && newErrors.length > 0}
                className="w-full bg-blue-600 text-white px-4 py-2 rounded-lg font-semibold hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                <Save className="w-4 h-4 mr-2 inline" />
                Agregar Proveedor
              </button>
              {Array.isArray(newErrors) && newErrors.length > 0 && (
                <div className="text-xs text-amber-700 dark:text-amber-300">{newErrors[0]}</div>
              )}
            </form>
          </div>
        </div>
      )}

      {/* Modal para ver/editar proveedor con actividad */}
      {editModal.show && editModal.supplier && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg w-full max-w-4xl relative grid grid-cols-1 md:grid-cols-2 gap-6">
            <button 
              onClick={() => setEditModal({ show: false, supplier: null })}
              className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 hover:dark:text-gray-300"
            >
              <X className="w-6 h-6" />
            </button>
            <h2 className="md:col-span-2 text-xl font-bold text-gray-800 dark:text-white mb-2">
              {editModal.supplier.readOnly ? 'Detalles del Proveedor' : 'Editar Proveedor'}
            </h2>
            <form onSubmit={editModal.supplier.readOnly ? null : handleUpdate} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Nombre del Proveedor</label>
                <input
                  type="text"
                  name="name"
                  value={editModal.supplier.name}
                  onChange={editModal.supplier.readOnly ? null : handleInputChange}
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 dark:border-gray-600 dark:bg-gray-700 dark:text-white ${
                    editModal.supplier.readOnly 
                      ? 'border-gray-300 bg-gray-100 dark:bg-gray-700' 
                      : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500 dark:border-gray-600'
                  }`}
                  readOnly={editModal.supplier.readOnly}
                  required={!editModal.supplier.readOnly}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Contacto Principal</label>
                <input
                  type="text"
                  name="contact"
                  value={editModal.supplier.contact}
                  onChange={editModal.supplier.readOnly ? null : handleInputChange}
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 dark:border-gray-600 dark:bg-gray-700 dark:text-white ${
                    editModal.supplier.readOnly 
                      ? 'border-gray-300 bg-gray-100 dark:bg-gray-700' 
                      : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500 dark:border-gray-600'
                  }`}
                  readOnly={editModal.supplier.readOnly}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Teléfono</label>
                <input
                  type="text"
                  name="phone"
                  value={editModal.supplier.phone}
                  onChange={editModal.supplier.readOnly ? null : handleInputChange}
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 dark:border-gray-600 dark:bg-gray-700 dark:text-white ${
                    editModal.supplier.readOnly 
                      ? 'border-gray-300 bg-gray-100 dark:bg-gray-700' 
                      : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500 dark:border-gray-600'
                  }`}
                  readOnly={editModal.supplier.readOnly}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Cuenta de Depósito</label>
                <input
                  type="text"
                  name="accountName"
                  value={editModal.supplier.accountName || ''}
                  onChange={editModal.supplier.readOnly ? null : handleInputChange}
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 dark:border-gray-600 dark:bg-gray-700 dark:text-white ${
                    editModal.supplier.readOnly 
                      ? 'border-gray-300 bg-gray-100 dark:bg-gray-700' 
                      : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500 dark:border-gray-600'
                  }`}
                  readOnly={editModal.supplier.readOnly}
                  placeholder="Ej: Firsbel"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">Cargos sugeridos para pedidos</label>
                <div className="space-y-2">
                  {(Array.isArray(editModal.supplier.defaultOrderCharges) ? editModal.supplier.defaultOrderCharges : []).map((charge, idx) => (
                    <div key={charge.id || idx} className="grid grid-cols-12 gap-2 p-2 border border-gray-200 dark:border-gray-600 rounded">
                      <select
                        value={charge.type || 'flat'}
                        onChange={(e) => updateEditSupplierCharge(idx, { type: e.target.value })}
                        disabled={editModal.supplier.readOnly}
                        className="col-span-3 text-xs px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white disabled:opacity-50"
                      >
                        <option value="percent">Porcentaje</option>
                        <option value="flat">Monto fijo</option>
                      </select>
                      <input
                        type="number"
                        value={charge.value || ''}
                        onChange={(e) => updateEditSupplierCharge(idx, { value: e.target.value })}
                        disabled={editModal.supplier.readOnly}
                        placeholder={charge.type === 'percent' ? '%' : '$'}
                        className="col-span-3 text-xs px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white disabled:opacity-50"
                        step="0.01"
                      />
                      <input
                        type="text"
                        value={charge.label || ''}
                        onChange={(e) => updateEditSupplierCharge(idx, { label: e.target.value })}
                        disabled={editModal.supplier.readOnly}
                        placeholder="Etiqueta"
                        className="col-span-5 text-xs px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white disabled:opacity-50"
                      />
                      {!editModal.supplier.readOnly && (
                        <button
                          type="button"
                          onClick={() => handleRemoveEditCharge(idx)}
                          className="col-span-1 text-red-600 dark:text-red-400 hover:text-red-800 hover:dark:text-red-300 flex items-center justify-center"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      )}
                    </div>
                  ))}
                  {!editModal.supplier.readOnly && (
                    <button
                      type="button"
                      onClick={handleAddEditCharge}
                      className="w-full text-sm py-2 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded text-gray-600 dark:text-gray-300 hover:border-blue-400 hover:text-blue-600 hover:dark:border-blue-500 hover:dark:text-blue-400 transition-colors"
                    >
                      + Agregar cargo
                    </button>
                  )}
                </div>
              </div>
              {!editModal.supplier.readOnly && Array.isArray(editErrors) && editErrors.length > 0 && (
                <div className="text-xs text-amber-700 dark:text-amber-300">{editErrors[0]}</div>
              )}
              {editModal.supplier.readOnly ? (
                <div className="flex justify-end">
                  <button 
                    onClick={() => setEditModal({ show: false, supplier: null })}
                    className="py-2 px-4 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-200 hover:bg-gray-50 hover:dark:bg-gray-700"
                  >
                    Cerrar
                  </button>
                </div>
              ) : (
                <button
                  type="submit"
                  disabled={Array.isArray(editErrors) && editErrors.length > 0}
                  className="w-full bg-green-600 text-white px-4 py-2 rounded-lg font-semibold hover:bg-green-700 transition-colors disabled:opacity-50"
                >
                  <Save className="w-4 h-4 mr-2 inline" />
                  Actualizar Proveedor
                </button>
              )}
            </form>
            <div className="space-y-3 max-h-[70vh] overflow-y-auto">
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">Actividad relacionada</h4>
              {transactions.filter(t => t.supplierId === editModal.supplier.id).slice(0, 50).map((t, idx) => {
                const deposited = Number(t.depositAmount || 0);
                const delivered = Number(t.deliveredAmount || 0);
                const pending = Math.max(0, Number(t.amount || 0) - delivered);
                return (
                  <div key={`${t.id}-${idx}`} className="p-3 border border-gray-100 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700">
                    <div className="flex items-center justify-between">
                      <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{t.type === 'sale' ? 'Venta' : 'Pedido'} • {t.date || 'Sin fecha'}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-300">{t.date || ''}</div>
                    </div>
                    <div className="text-sm text-gray-700 dark:text-gray-200">Monto: ${Number(t.amount || 0).toLocaleString()}</div>
                    <div className="text-xs text-gray-600 dark:text-gray-300">Depósitos: ${deposited.toLocaleString()} | Entregado: ${delivered.toLocaleString()} | Pendiente: ${pending.toLocaleString()}</div>
                  </div>
                );
              })}
              {transactions.filter(t => t.supplierId === editModal.supplier.id).length === 0 && (
                <div className="text-sm text-gray-500 dark:text-gray-400">Sin actividad para este proveedor.</div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Suppliers;